import { useEffect, useState } from "react";
import { Skeleton } from "@remo-co/ui-core/src/components/Skeleton";
import { Paper } from "@remo-co/ui-core/src/components/Paper";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import classNames from "classnames";
import { useI18n } from "i18n";
import InfoEdit from "./InfoEdit";
import InfoView from "./InfoView";
import { useStyles } from "./styles";
import { ISsoStatus } from "./SsoConfiguration";
import { ISsoInfo } from "./types";

interface IInfoContainerProps {
  isReady: boolean;
  ssoStatus: ISsoStatus;
  configInfo: ISsoInfo;

  isNew?: boolean;
  onSyncProvider: (data: Partial<ISsoInfo>) => Promise<boolean>;
}

const InfoContainer = ({
  isReady,
  configInfo,
  ssoStatus,
  isNew,
  onSyncProvider,
}: IInfoContainerProps) => {
  const classes = useStyles();
  const { t } = useI18n(["sso"]);
  const [isEditing, setIsEditing] = useState(isReady);
  const onToggleInfoEditing = () => setIsEditing(!isEditing);

  useEffect(() => {
    if (configInfo.providerId) {
      setIsEditing(false);
    } else {
      setIsEditing(isReady);
    }
  }, [configInfo.providerId, isReady]);

  // Skeleton loader
  if (!isReady) {
    return (
      <Paper
        className={classNames(classes.section, {
          [classes.spacing]: isEditing,
        })}
        elevation={0}
      >
        <div
          className={classNames(
            classes.split,
            classes.spacing,
            classes.bottomBorder,
          )}
        >
          <div>
            <Typography variant="h6">
              <strong>{t("sso:info.view.title")}</strong>
            </Typography>
            <Skeleton
              data-testid="skeleton-main"
              className={classes.headSpace}
              variant="rectangular"
              width="45px"
              height="20px"
            />
          </div>
        </div>
        <div className={classNames(classes.spacing)}>
          {[1, 2, 3].map((index) => (
            <div className={classes.readonlyField} key={index}>
              <Skeleton
                data-testid={`skeleton-${index}`}
                variant="text"
                width="100%"
                height="35px"
              />
            </div>
          ))}
        </div>
      </Paper>
    );
  }

  return (
    <Paper
      data-testid="info-container"
      className={classNames(classes.section, {
        [classes.spacing]: isEditing,
      })}
      elevation={0}
    >
      {isEditing ? (
        <InfoEdit
          configInfo={configInfo}
          isNew={isNew}
          onSyncProvider={onSyncProvider}
          onToggleInfoEditing={onToggleInfoEditing}
        />
      ) : (
        <InfoView
          configInfo={configInfo}
          ssoStatus={ssoStatus}
          onToggleInfoEditing={onToggleInfoEditing}
        />
      )}
    </Paper>
  );
};

export default InfoContainer;
